/**
 * Compact page header and simulation summary for the hybrid system page.
 * @module HybridSystemIntegration/components/MetricsDashboard
 */

import React from 'react';
import { Progress, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CONTROL_STRATEGIES } from '../constants';
import { roundMetric } from '../utils';
import styles from '../HybridSystemIntegration.module.css';

const { Paragraph, Text, Title } = Typography;

function SummaryMetric({ label, value, suffix, tone }) {
  return (
    <div className={styles.summaryMetric} data-tone={tone}>
      <Text className={styles.summaryMetricLabel}>{label}</Text>
      <div className={styles.summaryMetricValue}>
        <span>{value}</span>
        <small>{suffix}</small>
      </div>
    </div>
  );
}

/**
 * Top-level header and KPI summary.
 * @param {Object} props - Component props.
 * @returns {JSX.Element} Summary section.
 */
export function MetricsDashboard({ result, settings, strategy }) {
  const { t } = useTranslation();
  const strategyMeta = CONTROL_STRATEGIES[strategy] || CONTROL_STRATEGIES.heatPumpPriority;
  const renewableShare = Math.max(0, Math.min(100, roundMetric(result.renewableSharePercent, 0)));

  return (
    <section className={styles.summaryPanel}>
      <div className={styles.summaryHeader}>
        <div className={styles.summaryCopy}>
          <Title level={3}>{t('hybridSystemTool.hero.title')}</Title>
          <Paragraph>{t('hybridSystemTool.hero.subtitle')}</Paragraph>
        </div>

        <div className={styles.summaryContext}>
          <Tag>{roundMetric(settings.outdoorTemperature, 1)} C</Tag>
          <Tag>{settings.hourOfDay}:00</Tag>
          <Tag color={result.unmetLoadKw > 0.2 ? 'warning' : 'success'}>
            {result.unmetLoadKw > 0.2
              ? t('hybridSystemTool.status.unmet')
              : t('hybridSystemTool.status.covered')}
          </Tag>
        </div>
      </div>

      <div className={styles.summaryMetrics}>
        <SummaryMetric
          label={t('hybridSystemTool.metrics.currentCop')}
          value={roundMetric(result.cop, 1)}
          suffix="COP"
          tone="cool"
        />
        <SummaryMetric
          label={t('hybridSystemTool.metrics.heatDemand')}
          value={roundMetric(result.heatDemandKw, 1)}
          suffix="kW"
          tone="warm"
        />
        <SummaryMetric
          label={t('hybridSystemTool.metrics.cost')}
          value={roundMetric(result.costPerHour, 2)}
          suffix="EUR/h"
          tone="gold"
        />
        <SummaryMetric
          label={t('hybridSystemTool.metrics.carbon')}
          value={roundMetric(result.carbonKgPerHour, 2)}
          suffix="kg/h"
          tone="earth"
        />
      </div>

      <div className={styles.summaryFooter}>
        <div className={styles.summaryProgress}>
          <div className={styles.summaryProgressLabel}>
            <Text strong>{t('hybridSystemTool.metrics.renewableShare')}</Text>
            <Text type="secondary">{renewableShare}%</Text>
          </div>
          <Progress percent={renewableShare} showInfo={false} strokeColor="#6f8b74" />
        </div>

        <div
          className={styles.summaryStrategy}
          style={{ '--summary-strategy-color': strategyMeta.color }}
        >
          <Text type="secondary">{t('hybridSystemTool.controls.strategy')}</Text>
          <Text strong>{t(strategyMeta.translationKey)}</Text>
        </div>
      </div>
    </section>
  );
}

export default MetricsDashboard;
